import { useMemo, useState } from 'react'
import { useIntl } from 'umi'

import SummaryValues from '@/pages/dashboard/components/SummaryValues'
import { formatNum, formatPercentNum } from '@/pages/dashboard/utils/utils'
import { useLocal } from '@/pages/dashboard/utils/customHook'
import useSelectorDataNew from '@/pages/dashboard/filterPageComponents/multiTimezone/hooks/useSelectorDataNew'
import useOptions from './hooks/useOptions'
import CardBox from '@/pages/dashboard/components/CardBox'
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart'
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart'
import { formatDataByChartType } from '@/pages/dashboard/components/Chart/_config/plug';
import './index.scss'
import React from 'react'

interface MetricsTrendCardProps {
  data?: any
  loading?: boolean
  formValue?: any
  gameCode?: string
  selectMetrics?: any
  error?:boolean
}

export default function Zone(props: MetricsTrendCardProps) {
  const {
    data,
    loading,
    formValue,
    error,
    selectMetrics
  } = props
  const { isEN, getLocalText } = useLocal()

  const { formatMessage } = useIntl();
  const { selectorData } = useSelectorDataNew("zone");
  const [iseye, setIseye] = useState(true)
  
  const type = (() => {
    return formValue?.zone?.length > 1 ? 'multiple' : 'single'
  })()

  // #region 处理地区源数据
  const zoneDataFilter = (data, type) => {
    // 多国家逻辑
    if (type === 'multiple') {
      // 根据国家简写建立数据映射
      const legendMap = {}
      data?.metrics_list?.forEach(item => {
        const { zone } = item
        if (!legendMap[zone]) {
          legendMap[zone] = []
        }
        legendMap[zone].push(item)
      })
      // 取到筛选器中的地区对象
      const zoneRenderList = (() => {
        let result = selectorData?.zone_list?.filter(item => formValue?.zone?.includes(item?.value))

        return data?.top?.map(metricsKey => {
          return result?.filter(item => item?.value === metricsKey)[0]
        })
      })()
      const summaryMap = {}
      data?.aggregation?.forEach(item => {
        const { zone } = item
        if (!summaryMap[zone]) {
          summaryMap[zone] = []
        }
        summaryMap[zone].push(item)
      })

      return {
        chartData: zoneRenderList?.map(item => {
          return {
            name: (isEN ? item?.enText : item?.chText) + ` ${item?.deviation ? `(${item?.deviation})` : ''}`,
            data: legendMap[item?.value]?.map(dataItem => {
              return ({
                x: dataItem.date,
                y: dataItem?.metrics?.[selectMetrics?.value]?.value
              })
            })
          }
        }),
        summaryData: zoneRenderList?.map(item => {
          const sum = summaryMap[item?.value]?.filter(item => item?.type === 'sum')?.[0]?.metrics?.[selectMetrics?.value]?.value
          const avg = summaryMap[item?.value]?.filter(item => item?.type === 'avg_no_null')?.[0]?.metrics?.[selectMetrics?.value]?.value

          const formatCallback = (value) => {
            if (selectMetrics?.isPercent) {
              return formatPercentNum(value, selectMetrics?.decimals)
            }else {
              return formatNum(value, selectMetrics?.isDollar, selectMetrics?.isUserType)
            }
          }

          return {
            name: (isEN ? item?.enText : item?.chText) + ` ${item?.deviation ? `(${item?.deviation})` : ''}`,
            sum: `${getLocalText('daily_by_time_zone_trend_sum')} : ` + formatCallback(sum),
            avg: `${getLocalText('daily_by_time_zone_trend_avg')} : ` + formatCallback(avg),
          }
        })
      }
    }else {
      // 单国家逻辑

      // 接口反参字段
      const chartData = ['metrics_list', 'sdlw_metrics_list']
      const summaryData = ['avg_sum_metrics', 'sdlw_avg_sum_metrics']
      // 当前选中的地区
      const target = selectorData?.zoneList?.filter(item => {
        return item?.value === formValue?.zone?.[0]
      })?.[0]
      const zoneName = `${target?.[isEN ? 'enText' : 'chText']} (${target?.deviation})`
      const nameArr = [
        target?.value === '255' || !target ? `${getLocalText('daily_by_time_zone_selector_all')}` : zoneName,
        getLocalText('daily_by_time_zone_trend_sdlw')
      ]
      const xAxis1 = data?.[chartData[0]]?.map(item => item?.date)
      const xAxis2 = data?.[chartData[1]]?.map(item => item?.date)
      return {
        xAxis1,
        xAxis2,
        chartData: chartData?.map((key, index) => {
          return {
            name: nameArr[index],
            data: data?.[key]?.map(item => ({
              x: item?.date,
              y: item?.metrics?.[selectMetrics?.value]?.value
            }))
          }
        }),
        summaryData: summaryData?.map((key, index) => {
          const sum = data?.[key]?.[0]?.metrics?.[selectMetrics?.value]?.value
          const avg = data?.[key]?.[1]?.metrics?.[selectMetrics?.value]?.value
          const formatCallback = (value) => {
            if (selectMetrics?.isPercent) {
              return formatPercentNum(value, selectMetrics?.decimals)
            }else {
              return formatNum(value, selectMetrics?.isDollar, selectMetrics?.isUserType)
            }
          }

          return {
            name: nameArr[index],
            sum: `${getLocalText('daily_by_time_zone_trend_sum')} : ` + formatCallback(sum),
            avg: `${getLocalText('daily_by_time_zone_trend_avg')} : ` + formatCallback(avg),
          }
        })
      }
    }
  }
  // #endregion

  // #region 图表配置
  const { xAxis1, xAxis2, chartData, summaryData } = useMemo(() => {
    return zoneDataFilter(data, type)
  }, [selectMetrics?.value, data, type, isEN])
  const {
    config,
    options
  } = useOptions({
    data: {
      xAxis1,
      xAxis2,
      chartData
    },
    type,
    iseye,
    selectMetrics
  })
  // #endregion
  const lineChartConfig = formatDataByChartType('line', options?.chart, config[type]?.line)
  const barChartConfig = formatDataByChartType('bar', options?.chart, config[type]?.bar)
  const isHaveData = useMemo(()=>{
    return barChartConfig?.series?.map(((item:any) =>{
      return item?.data?.filter((items:any)=>{
        return items !== ''
      }) || []
      
    }))?.flat() || [];
  },[barChartConfig?.series])

  const isHaveDataFlag = isHaveData?.length>0;
  
  const renderList = [
    type === 'single' &&  {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      index: 1,
      content: formatMessage({ id: "common_bar_chart" }),
      component:
        <BarChart
          height={307}
          title={''}
          legendData={barChartConfig?.legend || []}
          xAxisData={barChartConfig?.xAxisData || []}
          series={barChartConfig?.series || []}
          formatterFunc={(params: any) => config[type].bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config[type].bar.setOtherConfigFunc(opt)}
        />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      index: 2,
      content: formatMessage({ id: "common_line_chart" }),
      component:
        <LineChart
          height={307}
          title={''}
          legendData={lineChartConfig?.legend || []}
          xAxisData={lineChartConfig?.xAxisData || []}
          series={lineChartConfig?.series || []}
          formatterFunc={(params: any) => config[type].line.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config[type].line.setOtherConfigFunc(opt)}
        />
      ,
    },
  ]
  return (
  <CardBox
      className='register-table'
      height={307}
      titleCom={isHaveDataFlag && <div className='dbd-time-zone-chart-title'>{selectMetrics?.text}</div>}
      middleContentCom={<><SummaryValues summaryList={summaryData} /></>}
      renderList={renderList}
      defaultAvtive={2}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye)
        }
      }}
      bordered={false}
      isLoading={loading}
      isError={error}
      isNodata={!isHaveDataFlag}
    />
  )
}
